import { format, parseISO } from 'date-fns';
import { ptBR } from 'date-fns/locale';
import {
  CartesianGrid,
  Line,
  LineChart,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from 'recharts';
import type { SummaryMonthlyPoint } from '@/features/summary/api';
import { formatBRL } from '@/lib/utils';

interface Props {
  points: SummaryMonthlyPoint[];
}

function formatMonthShort(month: string): string {
  return format(parseISO(`${month}-01`), 'MMM/yy', { locale: ptBR });
}

export function MonthlyChart({ points }: Props) {
  const data = points.map((p) => ({
    month: formatMonthShort(p.month),
    total: p.total,
  }));

  return (
    <section className="rounded-xl border border-(--color-border) bg-(--color-surface) p-5">
      <h2 className="mb-4 font-display text-lg font-semibold">
        Últimos 6 meses
      </h2>
      <div className="h-56 w-full">
        <ResponsiveContainer width="100%" height="100%">
          <LineChart data={data} margin={{ top: 8, right: 12, left: 0, bottom: 0 }}>
            <CartesianGrid strokeDasharray="3 3" stroke="var(--color-border)" />
            <XAxis
              dataKey="month"
              stroke="var(--color-fg-muted)"
              tick={{ fontSize: 12 }}
            />
            <YAxis
              stroke="var(--color-fg-muted)"
              tick={{ fontSize: 12 }}
              tickFormatter={(v) =>
                new Intl.NumberFormat('pt-BR', {
                  notation: 'compact',
                  maximumFractionDigits: 1,
                }).format(Number(v))
              }
              allowDecimals={false}
            />
            <Tooltip
              formatter={(value) => formatBRL(Number(value))}
              contentStyle={{
                background: 'var(--color-surface-elevated)',
                border: '1px solid var(--color-border)',
                borderRadius: 8,
              }}
              labelStyle={{ color: 'var(--color-fg-muted)' }}
            />
            <Line
              type="monotone"
              dataKey="total"
              stroke="var(--color-accent)"
              strokeWidth={2}
              dot={{ r: 3 }}
              activeDot={{ r: 5 }}
              isAnimationActive={false}
            />
          </LineChart>
        </ResponsiveContainer>
      </div>
    </section>
  );
}
